
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, AlertCircle } from "lucide-react";
import PasswordResetForm from "./PasswordResetForm";
import LoginSignupForm from "./LoginSignupForm";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useAuthHandlers } from "@/hooks/useAuthHandlers";

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [lastEmail, setLastEmail] = useState("");
  const {
    isRecoveryFlow,
    isPasswordReset,
    message,
    error,
    setIsRecoveryFlow,
    setIsPasswordReset,
    setMessage,
    setError
  } = useUrlParams();
  const { loading, setLoading, handleResendConfirmation } = useAuthHandlers();

  const handleBackToLogin = () => {
    setIsPasswordReset(false);
    setIsLogin(true);
    setIsRecoveryFlow(false);
    setError("");
    setMessage("");
    
    // Clear the URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setMessage("");
  };

  const handleSuccess = (successMessage?: string) => {
    if (successMessage) {
      setMessage(successMessage);
    }
    setError("");
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setMessage("");
  };

  const handleForgotPassword = () => {
    // This is handled within LoginSignupForm
  };

  const handleResendClick = async (email: string) => {
    setLoading(true);
    try {
      const message = await handleResendConfirmation(email);
      setMessage(message);
      setError("");
    } catch (err) {
      setError((err as Error).message);
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (isPasswordReset) return "Reset password";
    return isLogin ? "Welcome back" : "Get started";
  };

  const getDescription = () => {
    if (isPasswordReset) return "Enter your new password below";
    return isLogin 
      ? "Sign in to continue"
      : "Create your account";
  };

  // Check if message is about email verification
  const isEmailVerificationMessage = message && (
    message.includes("Check your email") || 
    message.includes("confirmation link") ||
    message.includes("verification") ||
    message.includes("reset email sent")
  );

  // Check if error is about email not confirmed
  const isEmailNotConfirmedError = error && error.toLowerCase().includes("email not confirmed");

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">
          {getTitle()}
        </h1>
        <p className="text-muted-foreground mt-2">
          {getDescription()}
        </p>
      </div>
      
      {/* Alerts */}
      <div className="space-y-4 mb-6">
        {error && !isEmailNotConfirmedError && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isEmailNotConfirmedError && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>Email not confirmed</span>
                <button
                  onClick={() => handleResendClick(lastEmail)}
                  disabled={loading}
                  className="text-sm underline ml-2 hover:no-underline"
                >
                  {loading ? "Sending..." : "Re-send"}
                </button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {message && isEmailVerificationMessage && (
          <Alert className="border-primary/20 bg-primary/5">
            <Mail className="h-4 w-4 text-primary" />
            <AlertDescription>
              <div className="space-y-1">
                <div className="font-medium">{message}</div>
                <div className="text-xs text-muted-foreground">
                  Check your spam folder if you don't see it
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {message && !isEmailVerificationMessage && (
          <Alert className="border-primary/20 bg-primary/5">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Form */}
      {isPasswordReset ? (
        <PasswordResetForm
          onError={handleError}
          onBackToLogin={handleBackToLogin}
        />
      ) : (
        <LoginSignupForm
          isLogin={isLogin}
          onToggleMode={handleToggleMode}
          onSuccess={handleSuccess}
          onError={(errorMessage) => {
            handleError(errorMessage);
            const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
            if (emailInput) {
              setLastEmail(emailInput.value);
            }
          }}
          onForgotPassword={handleForgotPassword}
        />
      )}
    </div>
  );
};

export default AuthForm;
